import { useState } from "react";

// Third Party
import { useMutation } from "@tanstack/react-query";
import clsx from "clsx";

// Hooks
import { useLocation } from "@/Common/hooks/Location/useLocation";

// Redux
import { useDispatch } from "react-redux";

// API
import { updateLocation } from "@/Common/api/location/mutations";

// MUI
import { Button, Stack } from "@mui/material";

// Components
import StoreDetails from "../StoreDetails";

// Helpers
import { and, or } from "@/Common/helpers/sonar";

// Icons
import { AddCircleRounded } from "@mui/icons-material";

// Types
import { IGeoDetails, ILocation, IStoreDetails } from "@/Common/interfaces/location";

// Actions
import { actions as ACTION_LOCATION } from "@/redux/reducers/Location";

const otherStoreNames = (stores: IStoreDetails[], currentIndex: number) => {
	return stores.filter((_, index) => index !== currentIndex).map((store) => store.name);
};

const MAX_STORES = 5;

const DEFAULT_GEO_DETAILS: IGeoDetails = {
	address: "",
	address2: "",
	city: "",
	state: "",
	zipCode: "",
	country: "",
	countryCode: "",
};

const EMPTY_STORE_DETAILS: IStoreDetails = {
	isDefaultStore: false,
	name: "",
	email: "",
	geoDetails: DEFAULT_GEO_DETAILS,
	timezone: "",
};

const LocationDetails = () => {
	const location = useLocation();
	const [storesDetails, setStoresDetails] = useState<IStoreDetails[]>(location?.storesDetails || []);
	const dispatch = useDispatch();
	const [hasStoreDetailsError, setHasStoreDetailsError] = useState<boolean>(false);

	/************************************************** MUTATIONS ******************************************************/

	const { mutate: updateStoresDetails, isPending: isUpdateStoresDetailsPending } = useMutation({
		mutationKey: ["UpdateStoreDetails", location?.locationId],
		mutationFn: async (newStoreDetails: IStoreDetails[]) => {
			const response = await updateLocation(location.locationId, { storesDetails: newStoreDetails }, "STORES_DETAILS");
			const { storesDetails: updatedStoresDetails = [] } = response as Partial<ILocation>;
			dispatch(ACTION_LOCATION.SET_LOCATION({ location: { ...location, storesDetails: updatedStoresDetails } }));
			return response;
		},
	});

	/*************************************************** HANDLERS ******************************************************/

	const handleAddNewStore = () => {
		const newStore: IStoreDetails = { ...EMPTY_STORE_DETAILS, isDefaultStore: !storesDetails?.length };
		setStoresDetails((prevStores) => [...prevStores, newStore]);
		setHasStoreDetailsError(true);
	};

	const handleRemoveStore = (index: number) => {
		const updatedStores = storesDetails.filter((_, i) => i !== index);
		setStoresDetails(updatedStores);
		if (hasStoreDetailsError) {
			return;
		}
		updateStoresDetails(updatedStores);
		setHasStoreDetailsError(false);
	};

	const handleSetDefaultStore = (index: number) => {
		const storeToSetDefault = storesDetails[index];
		if (!storeToSetDefault || hasStoreDetailsError) {
			return;
		}
		const updatedStores = storesDetails.map((store, storeIndex) => ({
			...store,
			isDefaultStore: storeIndex === index,
		}));
		setStoresDetails(updatedStores);
		updateStoresDetails(updatedStores);
	};

	const handleUpdateStoreDetails = (index: number, newStoreDetails: IStoreDetails) => {
		const updatedStores = storesDetails.map((store, storeIndex) => (storeIndex === index ? newStoreDetails : store));
		setStoresDetails(updatedStores);
		if (hasStoreDetailsError) {
			return;
		}
		updateStoresDetails(updatedStores);
	};

	const isMaxStoresReached = storesDetails.length >= MAX_STORES;
	const hasAtLeastOneStoreDetails = storesDetails?.length;
	const shouldBeEnabledAddStoreButton = and(!isUpdateStoresDetailsPending, !isMaxStoresReached, !hasStoreDetailsError);
	const shouldBeDisabledAddStoreButton = or(isMaxStoresReached, isUpdateStoresDetailsPending, hasStoreDetailsError);

	return (
		<Stack flexDirection={"column"} gap={3} width={"1086px"}>
			<Stack flexDirection={"column"} gap={6}>
				{storesDetails.map((store, index) => {
					const keyIndex = `store-detail-${index}`;

					if (!store) {
						return null;
					}

					return (
						<StoreDetails
							key={keyIndex}
							index={index}
							geoDetails={store?.geoDetails}
							timezone={store?.timezone || ""}
							otherStoreNames={otherStoreNames(storesDetails, index)}
							isUpdateDisabled={isUpdateStoresDetailsPending}
							storeDetails={store}
							onSetDefaultStore={() => handleSetDefaultStore(index)}
							onRemoveStore={() => handleRemoveStore(index)}
							onUpdateStoreDetails={(storeIndex, newStoreDetails: IStoreDetails) =>
								handleUpdateStoreDetails(storeIndex, newStoreDetails)
							}
							onUpdateStoreDetailsError={(hasError: boolean) => setHasStoreDetailsError(hasError)}
						/>
					);
				})}
			</Stack>
			<Stack flexDirection={"column"}>
				<Button
					onClick={handleAddNewStore}
					variant="contained"
					disabled={shouldBeDisabledAddStoreButton}
					disableElevation
					endIcon={<AddCircleRounded />}
					classes={{
						root: clsx("!rounded-md !w-[210px] !h-[30px] !text-sm !text-white !font-sans !font-normal !normal-case", {
							"!bg-primary hover:!shadow-primary-shadow-50": or(!hasAtLeastOneStoreDetails, shouldBeEnabledAddStoreButton),
							"!bg-disabled": shouldBeDisabledAddStoreButton,
						}),
					}}
				>
					Add Store Name and Address
				</Button>
			</Stack>
		</Stack>
	);
};

export default LocationDetails;
